// ClipAudioPlayer provides lightweight audio-only playback for QA and Video Performance
// Direction screens, letting reviewers scrub, restart, and download translated clips
// without loading a full video player.
import React, {
  useState,
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
  useCallback,
} from "react";
import classes from "./ClipAudioPlayer.module.css";
import { Grid, IconButton } from "@mui/material";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import RefreshIcon from "@mui/icons-material/Refresh";
import DownloadIcon from "@mui/icons-material/Download";
import StopIcon from "@mui/icons-material/Stop";
import { getExportChannelGain } from "../../utils/exportMixLevel";

const ClipAudioPlayer = forwardRef(
  (
    {
      src,
      decible,
      playbackVolume = 1,
      onPlay,
      onEnd,
      showScrubBar = true,
      autoPlayRequest,
      timeDecimalPlaces = 2,
      compact = false,
      embedded = false,
    },
    ref
  ) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [loadedPercentage, setLoadedPercentage] = useState(0);

  const audioContext = useRef(null);
  const gainNode = useRef(null);
  // Matches the export's mono-to-stereo level; set per decoded clip.
  const exportChannelGainNode = useRef(null);
  // Listening-only gain chained after the clip gain; never saved to the clip.
  const playbackGainNode = useRef(null);
  const source = useRef(null);
  const audioBuffer = useRef(null);
  const startTime = useRef(0);
  const pauseTime = useRef(0);
  const playbackRate = useRef(1);
  const animationRef = useRef(null);
  const rangeRef = useRef(null);
  const loadedSourceRef = useRef(null);
  const handledAutoPlayRequestRef = useRef(autoPlayRequest);
  const loadRequestIdRef = useRef(0);
  const pendingPlayRef = useRef(false);
  const pendingSeekRef = useRef(null);

  // Memoize the loader to satisfy the hooks dependency check.
  const loadAudio = useCallback(async () => {
    const loadRequestId = loadRequestIdRef.current + 1;
    loadRequestIdRef.current = loadRequestId;

    // Reset loading indicators before fetching the audio source.
    loadedSourceRef.current = null;
    setIsLoading(true);
    setIsLoaded(false);
    setLoadedPercentage(0);
    try {
      // Fetch the audio stream and validate the response.
      const response = await fetch(src);
      if (!response.ok) {
        throw new Error(`Failed to fetch audio: ${response.status}`);
      }

      let arrayBuffer;
      if (response.body) {
        // Capture content length (when available) to compute download progress.
        const contentLengthHeader = response.headers.get("Content-Length");
        const contentLength = contentLengthHeader
          ? parseInt(contentLengthHeader, 10)
          : null;

        // Read the stream in chunks to accumulate bytes and report progress.
        const reader = response.body.getReader();
        const chunks = [];
        let receivedLength = 0;

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          if (value) {
            chunks.push(value);
            receivedLength += value.length;

            // Update loaded percentage only when total size is known.
            if (contentLength && contentLength > 0) {
              const progress = (receivedLength / contentLength) * 100;
              setLoadedPercentage(progress);
            }
          }
        }

        // Merge streamed chunks into a single contiguous buffer for decoding.
        const combinedBuffer = new Uint8Array(receivedLength);
        let position = 0;
        for (const chunk of chunks) {
          combinedBuffer.set(chunk, position);
          position += chunk.length;
        }

        arrayBuffer = combinedBuffer.buffer;
      } else {
        // Fallback when streaming is unavailable: read the full array buffer.
        arrayBuffer = await response.arrayBuffer();
      }

      // Decode audio data and prepare playback metadata.
      const decodedAudio = await audioContext.current.decodeAudioData(arrayBuffer);

      if (loadRequestId !== loadRequestIdRef.current) {
        return;
      }

      audioBuffer.current = decodedAudio;
      exportChannelGainNode.current.gain.value =
        getExportChannelGain(decodedAudio);
      loadedSourceRef.current = src;
      setDuration(decodedAudio.duration);
      setIsLoaded(true);
      setLoadedPercentage(100); // Set to 100% when fully loaded
    } catch (error) {
      if (loadRequestId === loadRequestIdRef.current) {
        console.error("Error loading audio:", error);
      }
    } finally {
      // Ensure loading state is cleared regardless of outcome.
      if (loadRequestId === loadRequestIdRef.current) {
        setIsLoading(false);
      }
    }
  }, [src]);

  useEffect(() => {
    audioContext.current = new (window.AudioContext ||
      window.webkitAudioContext)();
    gainNode.current = audioContext.current.createGain();
    exportChannelGainNode.current = audioContext.current.createGain();
    playbackGainNode.current = audioContext.current.createGain();
    gainNode.current.connect(exportChannelGainNode.current);
    exportChannelGainNode.current.connect(playbackGainNode.current);
    playbackGainNode.current.connect(audioContext.current.destination);

    return () => {
      loadRequestIdRef.current += 1;
      if (audioContext.current) {
        audioContext.current.close();
      }
    };
  }, []);

  useEffect(() => {
    if (source.current) {
      source.current.stop();
      source.current = null;
    }
    setIsPlaying(false);
    setCurrentTime(0);
    pauseTime.current = 0;

    loadAudio();
  }, [loadAudio]);

  useEffect(() => {
    const updateTime = () => {
      if (isPlaying && audioBuffer.current) {
        const elapsed =
          (audioContext.current.currentTime - startTime.current) *
          playbackRate.current;
        const newCurrentTime = Math.min(elapsed + pauseTime.current, duration);
        setCurrentTime(newCurrentTime);

        if (newCurrentTime >= duration) {
          resetAudio();
          // Call the onEnd function when audio reaches the end
          if (onEnd) {
            onEnd();
          }
        } else {
          animationRef.current = requestAnimationFrame(updateTime);
        }
      }
    };

    if (isPlaying) {
      animationRef.current = requestAnimationFrame(updateTime);
    } else {
      cancelAnimationFrame(animationRef.current);
    }

    return () => cancelAnimationFrame(animationRef.current);
  }, [isPlaying, duration, onEnd]);

  const resetAudio = () => {
    setIsPlaying(false);
    setCurrentTime(0);
    pauseTime.current = 0;
    if (source.current) {
      source.current.stop();
      source.current = null;
    }
  };

  useEffect(() => {
    const updateRangeProgress = () => {
      if (rangeRef.current) {
        const progress = (currentTime / duration) * 100;
        rangeRef.current.style.setProperty("--range-progress", `${progress}%`);
        rangeRef.current.style.setProperty(
          "--range-loaded",
          `${loadedPercentage}%`
        );
      }
    };

    updateRangeProgress();
  }, [currentTime, duration, loadedPercentage]);

  const startPlayback = useCallback(() => {
    if (!isLoaded || isPlaying) return;

    source.current = audioContext.current.createBufferSource();
    source.current.buffer = audioBuffer.current;
    source.current.playbackRate.value = playbackRate.current;
    source.current.connect(gainNode.current);
    source.current.start(0, pauseTime.current);
    startTime.current = audioContext.current.currentTime;

    if (onPlay) {
      onPlay();
    }

    setIsPlaying(true);
  }, [isLoaded, isPlaying, onPlay]);

  // Fulfill preview play and seek requests that arrived while this source loaded.
  useEffect(() => {
    if (!isLoaded || loadedSourceRef.current !== src) {
      return;
    }

    if (pendingSeekRef.current !== null) {
      const safeSeekTime = Math.min(
        duration,
        Math.max(0, Number(pendingSeekRef.current) || 0)
      );

      pendingSeekRef.current = null;
      setCurrentTime(safeSeekTime);
      pauseTime.current = safeSeekTime;
    }

    if (pendingPlayRef.current) {
      pendingPlayRef.current = false;
      startPlayback();
    }
  }, [duration, isLoaded, src, startPlayback]);

  // Play a newly generated source once, after its replacement buffer is ready.
  useEffect(() => {
    if (
      !autoPlayRequest ||
      handledAutoPlayRequestRef.current === autoPlayRequest ||
      !isLoaded ||
      loadedSourceRef.current !== src
    ) {
      return;
    }

    handledAutoPlayRequestRef.current = autoPlayRequest;
    setCurrentTime(0);
    pauseTime.current = 0;
    startPlayback();
  }, [autoPlayRequest, isLoaded, src, startPlayback]);

  const pausePlayback = () => {
    pendingPlayRef.current = false;

    if (!isPlaying) return;

    if (source.current) {
      source.current.stop();
      source.current = null;
    }

    pauseTime.current = currentTime;
    setIsPlaying(false);
  };

  const stopPlayback = () => {
    pendingPlayRef.current = false;
    pendingSeekRef.current = null;
    resetAudio();
  };

  const changeVolume = (newVolume) => {
    if (gainNode.current) {
      const gainValue = newVolume / 100;
      gainNode.current.gain.setValueAtTime(
        gainValue,
        audioContext.current.currentTime
      );
    }
  };

  const seek = (seekTime) => {
    if (!isLoaded) {
      pendingSeekRef.current = seekTime;
      return;
    }

    pendingSeekRef.current = null;

    const safeSeekTime = Math.min(
      duration,
      Math.max(0, Number(seekTime) || 0)
    );

    setCurrentTime(safeSeekTime);
    pauseTime.current = safeSeekTime;

    if (isPlaying) {
      if (source.current) {
        source.current.stop();
      }
      source.current = audioContext.current.createBufferSource();
      source.current.buffer = audioBuffer.current;
      source.current.playbackRate.value = playbackRate.current;
      source.current.connect(gainNode.current);
      source.current.start(0, safeSeekTime);
      startTime.current = audioContext.current.currentTime;
    }
  };

  const setPlaybackRate = (requestedRate) => {
    const nextRate = Number(requestedRate);

    if (!Number.isFinite(nextRate) || nextRate <= 0) {
      return;
    }

    if (source.current && isPlaying) {
      const contextTime = audioContext.current.currentTime;
      const elapsedAtPreviousRate =
        (contextTime - startTime.current) * playbackRate.current;
      const restartTime = Math.min(
        duration,
        pauseTime.current + elapsedAtPreviousRate
      );

      source.current.stop();
      source.current = audioContext.current.createBufferSource();
      source.current.buffer = audioBuffer.current;
      source.current.playbackRate.value = nextRate;
      source.current.connect(gainNode.current);
      source.current.start(0, restartTime);
      pauseTime.current = restartTime;
      startTime.current = contextTime;
      setCurrentTime(restartTime);
    }

    playbackRate.current = nextRate;
  };

  // Expose the same playback controls the component supported originally.
  useImperativeHandle(ref, () => ({
    play: () => {
      audioContext.current?.resume?.();

      if (!isLoaded) {
        pendingPlayRef.current = true;
        return;
      }

      pendingPlayRef.current = false;
      if (!isPlaying) startPlayback();
    },
    pause: () => {
      if (isPlaying) pausePlayback();
    },
    stop: () => {
      stopPlayback();
    },
    unlock: () => audioContext.current?.resume?.(),
    setVolume: changeVolume,
    setPlaybackRate,
    seek: seek,
    getCurrentTime: () => currentTime,
    getDuration: () => duration,
    isPlaying: () => isPlaying,
    isLoaded: () => isLoaded,
  }));

  const formatTime = (time, decimalPlaces = timeDecimalPlaces) => {
    const clampedDecimalPlaces = Math.max(0, decimalPlaces);
    const safeTime = Number.isFinite(time) ? time : 0;
    return Number(safeTime).toFixed(clampedDecimalPlaces);
  };

  const handleDownload = async () => {
    try {
      const response = await fetch(src);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.style.display = "none";
      a.href = url;
      // Extract filename from the src URL or use a default name
      const fileName = src.split("/").pop() || "audio.mp3";
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error("Download failed:", error);
      // You might want to show an error message to the user here
    }
  };

  //Volume control
  useEffect(() => {
    if (decible !== null && decible !== undefined) {
      const gain = Math.pow(10, decible / 20); // gain = 10^(dB / 20)
      const newVolume = 100 * gain;
      changeVolume(Math.round(newVolume));
    }
  }, [decible]);

  useEffect(() => {
    if (!playbackGainNode.current) return;
    const nextGain = Number(playbackVolume);
    playbackGainNode.current.gain.setValueAtTime(
      Number.isFinite(nextGain) ? Math.min(2, Math.max(0, nextGain)) : 1,
      audioContext.current.currentTime
    );
  }, [playbackVolume]);

  const iconSize = compact ? "1.2rem" : "1.4rem";
  const iconButtonPadding = compact ? "2px" : "4px";

  return (
    <Grid
      container
      item
      direction="row"
      alignItems="center"
      justifyContent="space-evenly"
      wrap="nowrap"
      bgcolor="grey"
      style={{
        background: embedded ? "transparent" : "#F1F3F4",
        borderRadius: embedded ? 0 : "var(--radius-md)",
        height: compact ? "100%" : undefined,
      }}
      xs={10}
    >
      <Grid
        item
        xs={1}
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        {isLoading ? (
          <IconButton
            size="small"
            sx={{
              color: "black",
              padding: compact ? iconButtonPadding : undefined,
              "&:hover": {
                backgroundColor: "rgba(0, 0, 0, 0.04)",
              },
            }}
            disabled
          >
            <RefreshIcon sx={{ fontSize: iconSize }} />
          </IconButton>
        ) : isPlaying ? (
          <IconButton
            size="small"
            onClick={stopPlayback}
            sx={{
              color: "black",
              padding: iconButtonPadding,
              "&:hover": {
                backgroundColor: "rgba(0, 0, 0, 0.04)",
              },
            }}
          >
            <StopIcon sx={{ fontSize: iconSize }} />
          </IconButton>
        ) : (
          <IconButton
            size="small"
            onClick={startPlayback}
            disabled={!isLoaded}
            sx={{
              color: "black",
              padding: iconButtonPadding,
              "&:hover": {
                backgroundColor: "rgba(0, 0, 0, 0.04)",
              },
            }}
          >
            <PlayArrowRoundedIcon sx={{ fontSize: iconSize }} />
          </IconButton>
        )}
      </Grid>
      <Grid
        item
        fontSize={compact ? "14px" : "var(--font-size-normal)"}
        fontFamily="Livvic, sans-serif"
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <span>
          {formatTime(currentTime)} / {formatTime(duration)}
        </span>
      </Grid>

      {showScrubBar && (
        <Grid
          item
          xs={5}
          sx={{ flexGrow: 1, minWidth: 0 }}
        >
          <input
            ref={rangeRef}
            className={classes["custom-range-input"]}
            type="range"
            min="0"
            max={duration || 100}
            step={0.001}
            value={currentTime}
            onChange={(e) => seek(parseFloat(e.target.value))}
            disabled={!isLoaded}
          />
        </Grid>
      )}

      <Grid
        item
        xs={1}
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <IconButton
          onClick={handleDownload}
          size="small"
          sx={{
            color: "black",
            padding: compact ? iconButtonPadding : undefined,
            "&:hover": {
              backgroundColor: "rgba(0, 0, 0, 0.04)",
            },
          }}
        >
          <DownloadIcon sx={{ fontSize: iconSize }} />
        </IconButton>
      </Grid>
    </Grid>
  );
});

export default ClipAudioPlayer;
